import { Dropdown } from "@constellation-academy/epic-ui";
import { useMemo, useState } from "react";
import { useMutation, useRefetchableFragment } from "react-relay";
import { useTranslation } from "@i18n/hooks";
import type { createPersonalizedNotificationTemplateButton_CreateMutation } from "@relay/createPersonalizedNotificationTemplateButton_CreateMutation.graphql";
import type { createPersonalizedNotificationTemplateButton_PersonalizedNotificationTemplateFragment$key } from "@relay/createPersonalizedNotificationTemplateButton_PersonalizedNotificationTemplateFragment.graphql";
import type { createPersonalizedNotificationTemplateButton_Refetch } from "@relay/createPersonalizedNotificationTemplateButton_Refetch.graphql";
import {
	CREATE_PERSONALIZED_NOTIFICATION_TEMPLATE_MUTATION,
	PERSONALIZED_NOTIFICATION_TEMPLATE_FRAGMENT,
} from "./create-personalized-notification-template-button.graphql";
import {
	dropdownClass,
	wrapperClass,
} from "./create-personalized-notification-template-button.styles";
import type { CreatePersonalizedNotificationTemplateButtonProps } from "./create-personalized-notification-template-button.types";
import { Button } from "../../../../components/button";

export const CreatePersonalizedNotificationTemplateButton = ({
	availableTemplatesFragmentRef,
	connectionId,
}: CreatePersonalizedNotificationTemplateButtonProps) => {
	const { t } = useTranslation("common");
	const [selectedTemplateKey, setSelectedTemplateKey] = useState<string | undefined>();

	const [createPersonalizedNotificationTemplate, isCreatingPersonalizedNotificationTemplate] =
		useMutation<createPersonalizedNotificationTemplateButton_CreateMutation>(
			CREATE_PERSONALIZED_NOTIFICATION_TEMPLATE_MUTATION,
		);

	const [availableTemplates, refetch] = useRefetchableFragment<
		createPersonalizedNotificationTemplateButton_Refetch,
		createPersonalizedNotificationTemplateButton_PersonalizedNotificationTemplateFragment$key
	>(PERSONALIZED_NOTIFICATION_TEMPLATE_FRAGMENT, availableTemplatesFragmentRef);

	const options = useMemo(
		() =>
			availableTemplates.Admin.Notification.GetAvailableNotificationTemplates.map(
				(template) => ({
					value: template.key,
					label: template.title,
				}),
			),
		[availableTemplates],
	);

	const handleOnChange = (value: string) => {
		setSelectedTemplateKey(value);
	};

	const handleOnClick = () => {
		if (!selectedTemplateKey) return;

		createPersonalizedNotificationTemplate({
			variables: {
				input: {
					key: selectedTemplateKey,
				},
				connections: [connectionId],
			},
			onCompleted: () => {
				setSelectedTemplateKey(undefined);
				refetch({}, { fetchPolicy: "network-only" });
			},
		});
	};

	if (options.length === 0) {
		return null;
	}

	return (
		<div className={wrapperClass}>
			<Dropdown
				placeholder={t("personalizedNotificationTemplates.selectTemplate")}
				value={selectedTemplateKey}
				onChange={handleOnChange}
				options={options}
				className={dropdownClass}
			/>
			<Button
				disabled={!selectedTemplateKey}
				label={t("personalizedNotificationTemplates.createTemplate")}
				loading={isCreatingPersonalizedNotificationTemplate}
				onClick={handleOnClick}
			/>
		</div>
	);
};
